import React from "react";

/**
 * Рамка вокруг аватара (косметика из магазина профиля).
 *
 * Порт AvatarFrame из Маськи (те же ключи/цвета/анимации) + три рамки за
 * звёзды (royal/galaxy/phoenix), которых в компоненте Маськи не было, и
 * котик рамки "maska" перерисован inline-SVG (twemoji в Рублоксе нет).
 * Всё на чистом CSS (keyframes) — лёгкое, обтекает аватар любого размера.
 *
 * frame — ключ рамки ("gold", "orbit"…) или style-строка ("solid:#hex").
 */

// Конфиг рамок: kind — тип эффекта; остальное — параметры.
const FRAMES = {
  none:    { kind: "none" },
  silver:  { kind: "solid", colors: ["#c0c5cc"] },
  gold:    { kind: "solid", colors: ["#f2c14e"] },
  coral:   { kind: "solid", colors: ["#e8445a"] },
  ocean:   { kind: "spin",  colors: ["#37c6ff", "#3b82f6", "#00e5ff", "#37c6ff"], dur: 4 },
  sunset:  { kind: "spin",  colors: ["#ff7a8c", "#f4a261", "#ffd166", "#ff7a8c"], dur: 5 },
  neon:    { kind: "glow",  colors: ["#00e5ff", "#a855f7"], glow: "#a855f7" },
  rainbow: { kind: "rainbow" },
  fire:    { kind: "fire",  colors: ["#ff512f", "#f09819", "#ffd166"] },
  legend:  { kind: "legend" },
  // — премиум с движением —
  orbit:    { kind: "orbit",   base: "#1b2a4a", dot: "#37c6ff", dur: 3 },
  comets:   { kind: "comets",  base: "#2a1a3a", dot: "#ff5db1", dur: 2.4 },
  runlights:{ kind: "runlights", base: "#222", on: "#ffd166", off: "#7a5a10" },
  snake:    { kind: "snake",   base: "#0d2a1a", glow: "#39ff14", dur: 2.6 },
  stardust: { kind: "stardust", base: "#1a1030", dot: "#ffe6a8", dur: 5 },
  maska:    { kind: "cat",     base: "#3a1530", dot: "#ff7ac0", dur: 4 },
  // — премиум за звёзды —
  royal:   { kind: "royal",   dur: 6 },
  galaxy:  { kind: "galaxy",  base: "#120a2e", dot: "#b7a4ff", dur: 6 },
  phoenix: { kind: "phoenix", dot: "#ffb347", dur: 1.8 },
};

// keyframes (вставляются один раз через <style>)
const KEYFRAMES = `
@keyframes rb-frame-spin { to { transform: rotate(360deg); } }
@keyframes rb-frame-glow {
  0%,100% { box-shadow: 0 0 4px 1px var(--rbf-glow), inset 0 0 3px var(--rbf-glow); }
  50%     { box-shadow: 0 0 12px 4px var(--rbf-glow), inset 0 0 6px var(--rbf-glow); }
}
@keyframes rb-frame-flicker {
  0%,100% { filter: brightness(1)   saturate(1.1); }
  40%     { filter: brightness(1.3) saturate(1.4); }
  70%     { filter: brightness(0.95) saturate(1.2); }
}
@keyframes rb-orbit-twinkle {
  0%,100% { opacity: 1; }
  50%     { opacity: 0.4; }
}
@keyframes rb-runlight {
  0%,100% { opacity: 0.25; }
  50%     { opacity: 1; }
}
`;

// Вспомогательное: вращающийся контейнер, в котором "спутник" висит на радиусе.
function Spinner({ dur, delay = 0, reverse = false, children, style }) {
  return (
    <div style={{
      position: "absolute", inset: 0, borderRadius: "50%",
      animation: `rb-frame-spin ${dur}s linear ${delay}s infinite${reverse ? " reverse" : ""}`,
      ...style,
    }}>
      {children}
    </div>
  );
}

// Точка-спутник на верхней кромке кольца (за счёт вращения родителя — летит по кругу)
function Satellite({ color, size, glow = true, tail = false, content = null }) {
  return (
    <div style={{
      position: "absolute", top: -1, left: "50%", transform: "translateX(-50%)",
      width: tail ? size * 4 : size, height: size, borderRadius: size,
      background: tail
        ? `linear-gradient(90deg, transparent, ${color})`
        : color,
      boxShadow: glow ? `0 0 ${size * 1.6}px ${size * 0.4}px ${color}` : "none",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size, lineHeight: 1,
    }}>
      {content}
    </div>
  );
}

// Котик для рамки «Маська» — inline SVG (в Рублоксе нет twemoji-ассетов)
function CatHead({ size }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24"
         style={{ filter: "drop-shadow(0 0 3px #ff7ac0)" }}>
      <path d="M4 9 3 3l5 3h8l5-3-1 6a9 8.2 0 1 1-16 0Z" fill="#ff9dd0" />
      <path d="M4.6 4.6 7.5 6.3 5.2 7.7Z M19.4 4.6 16.5 6.3l2.3 1.4Z" fill="#e85aa8" />
      <circle cx="8.6" cy="12" r="1.4" fill="#3a1530" />
      <circle cx="15.4" cy="12" r="1.4" fill="#3a1530" />
      <path d="M10.8 15.6c.6.7 1.8.7 2.4 0" stroke="#3a1530" strokeWidth="1.1"
            strokeLinecap="round" fill="none" />
      <path d="M11.2 14.2h1.6l-.8 1Z" fill="#e8445a" />
    </svg>
  );
}

// Корона для рамки «Корона» — inline SVG
function Crown({ size }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24"
         style={{ filter: "drop-shadow(0 0 3px #f5b50a)" }}>
      <path d="M3 8l4.5 4L12 5l4.5 7L21 8l-1.8 10H4.8L3 8Z" fill="#f5b50a" />
      <circle cx="12" cy="14.5" r="1.5" fill="#e8445a" />
    </svg>
  );
}

export default function AvatarFrame({ frame, size = 48, thickness = 3, children, style }) {
  const key = frame || "none";
  const cfg = FRAMES[key];

  // нет рамки / неизвестный ключ без style-строки
  if (key === "none" || (!cfg && !(typeof key === "string" && key.includes(":")))) {
    return <div style={{ width: size, height: size, ...style }}>{children}</div>;
  }

  const ringStyle = {
    width: size, height: size, borderRadius: "50%",
    padding: thickness, boxSizing: "border-box", flexShrink: 0,
    display: "flex", alignItems: "center", justifyContent: "center",
    position: "relative",
    ...style,
  };

  // Аватар поверх (никогда не вращается)
  const avatar = (
    <div style={{
      position: "relative", zIndex: 2,
      width: "100%", height: "100%", borderRadius: "50%", overflow: "hidden",
      boxShadow: "0 0 0 1.5px rgba(255,255,255,0.6) inset",
    }}>
      {children}
    </div>
  );

  // размер летающих элементов масштабируем от рамки
  const dotSize = Math.max(3, Math.round(size * 0.09));

  // фон-кольцо (статичный или вращающийся градиент)
  let ringBg = null;
  let bgAnimation = null;
  let glowColor = null;
  let movers = null; // движущиеся элементы поверх кольца

  if (!cfg) {
    // совместимость со style-строками
    if (key.startsWith("solid:")) ringBg = key.slice(6);
    else if (key.startsWith("gradient:")) ringBg = `linear-gradient(135deg, ${key.slice(9)})`;
    else if (key === "rainbow") ringBg = "conic-gradient(#ff0040,#ff8a00,#ffe600,#00d26a,#00b3ff,#a855f7,#ff0040)";
  } else if (cfg.kind === "solid") {
    ringBg = cfg.colors[0];
  } else if (cfg.kind === "spin") {
    ringBg = `conic-gradient(${cfg.colors.join(",")})`;
    bgAnimation = `rb-frame-spin ${cfg.dur}s linear infinite`;
  } else if (cfg.kind === "glow") {
    ringBg = `linear-gradient(135deg, ${cfg.colors.join(",")})`;
    glowColor = cfg.glow;
  } else if (cfg.kind === "rainbow") {
    ringBg = "conic-gradient(#ff0040,#ff8a00,#ffe600,#00d26a,#00b3ff,#a855f7,#ff0040)";
    bgAnimation = "rb-frame-spin 3s linear infinite";
  } else if (cfg.kind === "fire") {
    ringBg = `conic-gradient(${cfg.colors.join(",")},${cfg.colors[0]})`;
    bgAnimation = "rb-frame-spin 6s linear infinite, rb-frame-flicker 1.2s ease-in-out infinite";
  } else if (cfg.kind === "legend") {
    ringBg = "conic-gradient(#f2c14e,#ff8a00,#e8445a,#a855f7,#f2c14e)";
    bgAnimation = "rb-frame-spin 4s linear infinite, rb-frame-flicker 1.5s ease-in-out infinite";
    glowColor = "#f2c14e";
  }
  // — премиум с движущимися элементами —
  else if (cfg.kind === "orbit") {
    // тёмное кольцо + одна светящаяся точка-спутник по орбите
    ringBg = `radial-gradient(circle, ${cfg.base}, ${cfg.base})`;
    glowColor = cfg.dot;
    movers = (
      <Spinner dur={cfg.dur}>
        <Satellite color={cfg.dot} size={dotSize * 1.3} />
      </Spinner>
    );
  } else if (cfg.kind === "comets") {
    // три кометы с хвостами, разнесённые по кругу
    ringBg = cfg.base;
    glowColor = cfg.dot;
    movers = (
      <>
        <Spinner dur={cfg.dur}><Satellite color={cfg.dot} size={dotSize} tail /></Spinner>
        <Spinner dur={cfg.dur} delay={-cfg.dur / 3}><Satellite color="#ffd166" size={dotSize} tail /></Spinner>
        <Spinner dur={cfg.dur} delay={-2 * cfg.dur / 3}><Satellite color="#7af" size={dotSize} tail /></Spinner>
      </>
    );
  } else if (cfg.kind === "runlights") {
    // бегущая гирлянда: 12 огоньков по кругу, пульсируют волной
    ringBg = cfg.base;
    const N = 12;
    movers = (
      <div style={{ position: "absolute", inset: 0 }}>
        {Array.from({ length: N }).map((_, i) => (
          <div key={i} style={{
            position: "absolute", inset: 0,
            transform: `rotate(${(360 / N) * i}deg)`,
          }}>
            <div style={{
              position: "absolute", top: -1, left: "50%", transform: "translateX(-50%)",
              width: dotSize, height: dotSize, borderRadius: dotSize,
              background: cfg.on, boxShadow: `0 0 ${dotSize}px ${cfg.on}`,
              animation: `rb-runlight 1s linear ${(i / N) * 1}s infinite`,
            }} />
          </div>
        ))}
      </div>
    );
  } else if (cfg.kind === "snake") {
    // ползущая яркая дуга (через conic-gradient: яркий сегмент крутится)
    ringBg = `conic-gradient(${cfg.glow} 0deg, ${cfg.glow} 40deg, ${cfg.base} 60deg, ${cfg.base} 360deg)`;
    bgAnimation = `rb-frame-spin ${cfg.dur}s linear infinite`;
    glowColor = cfg.glow;
  } else if (cfg.kind === "stardust") {
    // звёздная пыль: множество мерцающих искр на медленной орбите
    ringBg = `radial-gradient(circle, ${cfg.base}, ${cfg.base})`;
    const N = 8;
    movers = (
      <Spinner dur={cfg.dur}>
        {Array.from({ length: N }).map((_, i) => (
          <div key={i} style={{
            position: "absolute", inset: 0, transform: `rotate(${(360 / N) * i}deg)`,
          }}>
            <div style={{
              position: "absolute", top: -1, left: "50%", transform: "translateX(-50%)",
              width: dotSize * 0.8, height: dotSize * 0.8, borderRadius: dotSize,
              background: cfg.dot, boxShadow: `0 0 ${dotSize}px ${cfg.dot}`,
              animation: `rb-orbit-twinkle ${1 + (i % 3) * 0.4}s ease-in-out ${i * 0.2}s infinite`,
            }} />
          </div>
        ))}
      </Spinner>
    );
  } else if (cfg.kind === "cat") {
    // фирменная рамка Маськи: котик летит по кругу + розовое свечение
    ringBg = `conic-gradient(#ff7ac0,#ffd1ec,#ff7ac0,#c44ea0,#ff7ac0)`;
    bgAnimation = `rb-frame-spin 8s linear infinite`;
    glowColor = cfg.dot;
    const catSize = Math.max(10, Math.round(size * 0.22));
    movers = (
      <Spinner dur={cfg.dur}>
        <div style={{
          position: "absolute", top: -catSize * 0.45, left: "50%",
          transform: "translateX(-50%)", width: catSize, height: catSize,
        }}>
          <CatHead size={catSize} />
        </div>
      </Spinner>
    );
  } else if (cfg.kind === "royal") {
    // корона: золото-пурпурное кольцо + корона, плывущая по кругу
    ringBg = "conic-gradient(#f5b50a,#ffd166,#a855f7,#f5b50a)";
    bgAnimation = `rb-frame-spin ${cfg.dur}s linear infinite`;
    glowColor = "#f5b50a";
    const crownSize = Math.max(10, Math.round(size * 0.22));
    movers = (
      <Spinner dur={cfg.dur} reverse>
        <div style={{
          position: "absolute", top: -crownSize * 0.45, left: "50%",
          transform: "translateX(-50%)", width: crownSize, height: crownSize,
        }}>
          <Crown size={crownSize} />
        </div>
      </Spinner>
    );
  } else if (cfg.kind === "galaxy") {
    // галактика: тёмное кольцо-спираль + две звезды на встречных орбитах
    ringBg = `conic-gradient(${cfg.base},#3b2a6e,#6d28d9,${cfg.base},#274690,${cfg.base})`;
    bgAnimation = `rb-frame-spin ${cfg.dur * 2}s linear infinite`;
    glowColor = "#6d28d9";
    movers = (
      <>
        <Spinner dur={cfg.dur}><Satellite color={cfg.dot} size={dotSize} /></Spinner>
        <Spinner dur={cfg.dur * 1.6} reverse delay={-1}>
          <Satellite color="#37c6ff" size={dotSize * 0.8} />
        </Spinner>
      </>
    );
  } else if (cfg.kind === "phoenix") {
    // феникс: огненное кольцо + быстрая комета с длинным хвостом
    ringBg = "conic-gradient(#ff512f,#f09819,#ffd166,#ff512f)";
    bgAnimation = "rb-frame-spin 5s linear infinite, rb-frame-flicker 1s ease-in-out infinite";
    glowColor = cfg.dot;
    movers = (
      <Spinner dur={cfg.dur}>
        <Satellite color={cfg.dot} size={dotSize * 1.2} tail />
      </Spinner>
    );
  }

  const spinning = bgAnimation && bgAnimation.includes("spin");

  return (
    <div style={ringStyle}>
      <style>{KEYFRAMES}</style>
      {/* фон-кольцо */}
      <div style={{
        position: "absolute", inset: 0, borderRadius: "50%", zIndex: 0,
        background: ringBg,
        animation: bgAnimation || undefined,
        ...(glowColor ? { "--rbf-glow": glowColor } : {}),
        ...(glowColor && !spinning && !movers ? { animation: "rb-frame-glow 1.6s ease-in-out infinite" } : {}),
        ...(glowColor && (spinning || movers) ? { boxShadow: `0 0 10px 2px ${glowColor}` } : {}),
      }} />
      {/* движущиеся элементы (орбиты/кометы/искры/котик) — слой 1, под аватаром */}
      {movers && <div style={{ position: "absolute", inset: 0, zIndex: 1, borderRadius: "50%" }}>{movers}</div>}
      {avatar}
    </div>
  );
}
